import {NgModule} from '@angular/core';
import {PreloadAllModules, RouterModule, Routes} from '@angular/router';
import {TokenUserInfo} from './shared/services/tokenUserInfo.service';
import {FreshpassFaqComponent} from './shared/components/freshpass-faq/freshpass-faq.component';
import {properties} from 'src/environments/properties';

// Function to get the program path based on flag
function getProgramPath(): string {
    return properties.featureFlags.enableForUPlus ? 'foruplus' : 'freshpass';
}

// Function to generate routes dynamically
function generateRoutes(): Routes {
    const programPath = getProgramPath();
    
    return [
    // Main routes with dynamic path
    {
        path: `${programPath}/manage`,
        loadChildren: () => import('./components/manage/manage-subscriptions.module').then(m => m.ManageSubscriptionsModule),
        resolve: {
            tokenObject: TokenUserInfo
        },
    },
    {
        path: `${programPath}/manage.html`,
        loadChildren: () => import('./components/manage/manage-subscriptions.module').then(m => m.ManageSubscriptionsModule),
        resolve: {
            tokenObject: TokenUserInfo
        },
    },
    {
        path: `${programPath}/manage/change`,
        loadChildren: () => import('./components/change-plan/change-plan.module').then(m => m.ChangePlanModule),
        resolve: {
            tokenObject: TokenUserInfo
        },
    },
    {
        path: `${programPath}/manage/change.html`,
        loadChildren: () => import('./components/change-plan/change-plan.module').then(m => m.ChangePlanModule),
        resolve: {
            tokenObject: TokenUserInfo
        },
    },
    {
        path: `${programPath}/manage/confirmation`,
        loadChildren: () => import('./components/confirmation/confirmation.module').then(m => m.ConfirmationModule),
        resolve: {
            tokenObject: TokenUserInfo
        },
    },
    {
        path: `${programPath}/manage/confirmation.html`,
        loadChildren: () => import('./components/confirmation/confirmation.module').then(m => m.ConfirmationModule),
        resolve: {
            tokenObject: TokenUserInfo
        },
    },
    {
        path: `${programPath}/manage/cancel-plan`,
        loadChildren: () => import('./components/cancel-freshpass/cancel-freshpass.module').then(m => m.CancelFreshpassModule),
        resolve: {
            tokenObject: TokenUserInfo
        },
    },
    {
        path: `${programPath}/manage/cancel-plan.html`,
        loadChildren: () => import('./components/cancel-freshpass/cancel-freshpass.module').then(m => m.CancelFreshpassModule),
        resolve: {
            tokenObject: TokenUserInfo
        },
    },
    {
        path: `${programPath}/manage/update-payments`,
        loadChildren: () => import('./components/update-payments/update-payments.module').then(m => m.UpdatePaymentsModule),
        resolve: {
            tokenObject: TokenUserInfo
        },
    },
    {
        path: `${programPath}/manage/update-payments.html`,
        loadChildren: () => import('./components/update-payments/update-payments.module').then(m => m.UpdatePaymentsModule),
        resolve: {
            tokenObject: TokenUserInfo
        }
    },
    {
        path: `${programPath}/manage/subscribe`,
        loadChildren: () => import('./components/registration/registration.module').then(m => m.RegistrationModule),
    },
    {
        path: `${programPath}/manage/subscribe.html`,
        loadChildren: () => import('./components/registration/registration.module').then(m => m.RegistrationModule),
    },
    {
        path: `${programPath}/manage/${programPath}-faq`,
        component: FreshpassFaqComponent
    },
    {
        path: `${programPath}/manage/${programPath}-faq.html`,
        component: FreshpassFaqComponent
    },
    {
        path: `${programPath}/manage/govassist`,
        loadChildren: () => import('./components/snap/snap.module').then(m => m.SnapModule),
    },
    {
        path: `${programPath}/manage/govassist.html`,
        loadChildren: () => import('./components/snap/snap.module').then(m => m.SnapModule),
    },
    {
        path: `${programPath}/manage/discount`,
        loadChildren: () => import('./components/partners/partners.module').then(m => m.PartnersModule),
    },
    {
        path: `${programPath}/manage/discount.html`,
        loadChildren: () => import('./components/partners/partners.module').then(m => m.PartnersModule),
    },
    
    // Fallback redirects for legacy paths
    ...generateLegacyRedirects(programPath)
];
}

// Function to generate redirects for the legacy path
function generateLegacyRedirects(currentProgramPath: string): Routes {
    const legacyPath = currentProgramPath === 'foruplus' ? 'freshpass' : 'foruplus';
    
    return [
        // Redirect legacy paths to current paths
        { path: `${legacyPath}/manage`, redirectTo: `${currentProgramPath}/manage`, pathMatch: 'full' },
        { path: `${legacyPath}/manage.html`, redirectTo: `${currentProgramPath}/manage.html`, pathMatch: 'full' },
        { path: `${legacyPath}/manage/change`, redirectTo: `${currentProgramPath}/manage/change`, pathMatch: 'full' },
        { path: `${legacyPath}/manage/change.html`, redirectTo: `${currentProgramPath}/manage/change.html`, pathMatch: 'full' },
        { path: `${legacyPath}/manage/confirmation`, redirectTo: `${currentProgramPath}/manage/confirmation`, pathMatch: 'full' },
        { path: `${legacyPath}/manage/confirmation.html`, redirectTo: `${currentProgramPath}/manage/confirmation.html`, pathMatch: 'full' },
        { path: `${legacyPath}/manage/cancel-plan`, redirectTo: `${currentProgramPath}/manage/cancel-plan`, pathMatch: 'full' },
        { path: `${legacyPath}/manage/cancel-plan.html`, redirectTo: `${currentProgramPath}/manage/cancel-plan.html`, pathMatch: 'full' },
        { path: `${legacyPath}/manage/update-payments`, redirectTo: `${currentProgramPath}/manage/update-payments`, pathMatch: 'full' },
        { path: `${legacyPath}/manage/update-payments.html`, redirectTo: `${currentProgramPath}/manage/update-payments.html`, pathMatch: 'full' },
        { path: `${legacyPath}/manage/subscribe`, redirectTo: `${currentProgramPath}/manage/subscribe`, pathMatch: 'full' },
        { path: `${legacyPath}/manage/subscribe.html`, redirectTo: `${currentProgramPath}/manage/subscribe.html`, pathMatch: 'full' },
        { path: `${legacyPath}/manage/${legacyPath}-faq`, redirectTo: `${currentProgramPath}/manage/${currentProgramPath}-faq`, pathMatch: 'full' },
        { path: `${legacyPath}/manage/${legacyPath}-faq.html`, redirectTo: `${currentProgramPath}/manage/${currentProgramPath}-faq.html`, pathMatch: 'full' },
        { path: `${legacyPath}/manage/govassist`, redirectTo: `${currentProgramPath}/manage/govassist`, pathMatch: 'full' },
        { path: `${legacyPath}/manage/govassist.html`, redirectTo: `${currentProgramPath}/manage/govassist.html`, pathMatch: 'full' },
        { path: `${legacyPath}/manage/discount`, redirectTo: `${currentProgramPath}/manage/discount`, pathMatch: 'full' },
        { path: `${legacyPath}/manage/discount.html`, redirectTo: `${currentProgramPath}/manage/discount.html`, pathMatch: 'full' }
    ];
}

@NgModule({
    imports: [RouterModule.forRoot(generateRoutes(),
        {
            scrollPositionRestoration: 'enabled',
            preloadingStrategy: PreloadAllModules
        })],
    exports: [RouterModule]
})
export class AppRoutingModule {
}

